import type { ConceptSlug, LessonAccess } from "../types";

export type ConceptSectionId =
  | "goals-and-execution"
  | "makefiles-and-parsing"
  | "managed-installations"
  | "recipes-and-shells"
  | "repository-practice"
  | "rules-and-targets";

export interface ConceptSectionDefinition {
  description: string;
  id: ConceptSectionId;
  title: string;
}

export interface ConceptDefinition {
  access: LessonAccess;
  description: string;
  officialReference?: {
    href: string;
    label?: string;
    title: string;
  };
  lessonCommits: readonly string[];
  sectionId: ConceptSectionId;
  slug: ConceptSlug;
  title: string;
}

export const conceptSections = [
  {
    description:
      "Understand what a makefile describes and how GNU Make finds the file it reads.",
    id: "makefiles-and-parsing",
    title: "Makefiles and parsing",
  },
  {
    description:
      "Understand the parts of a rule and the special targets used to name build behavior.",
    id: "rules-and-targets",
    title: "Rules and targets",
  },
  {
    description:
      "Follow how selected goals lead Make through the dependency graph in a particular run.",
    id: "goals-and-execution",
    title: "Goals and execution",
  },
  {
    description:
      "See where Make expansion ends and the recipe shell's variables, processes, and sourced state begin.",
    id: "recipes-and-shells",
    title: "Recipes and shells",
  },
  {
    description:
      "Build reproducible tool installations from verified artifacts, immutable releases, and stable command paths.",
    id: "managed-installations",
    title: "Managed installations",
  },
  {
    description:
      "Keep repository instructions and review boundaries explicit as conventions grow across languages and areas.",
    id: "repository-practice",
    title: "Repository practice",
  },
] as const satisfies readonly ConceptSectionDefinition[];

export const conceptDefinitions = [
  {
    access: "free",
    description:
      "Understand the file that tells `make` how to build a project and run other project tasks.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Introduction.html",
      title: "An Introduction to Makefiles",
    },
    lessonCommits: ["1f83aca", "9981476"],
    sectionId: "makefiles-and-parsing",
    slug: "makefiles",
    title: "Makefiles",
  },
  {
    access: "free",
    description:
      "See how `make` searches for a makefile when no arguments are provided.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Makefile-Names.html",
      title: "What Name to Give Your Makefile",
    },
    lessonCommits: ["1f83aca", "9981476"],
    sectionId: "makefiles-and-parsing",
    slug: "makefile-discovery",
    title: "Makefile discovery",
  },
  {
    access: "paid",
    description:
      "Resolve included makefiles from a stable anchor instead of depending on the invocation directory.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Include.html",
      title: "Including Other Makefiles",
    },
    lessonCommits: ["7bf2533", "6efe9dc", "6cd3401", "9981476"],
    sectionId: "makefiles-and-parsing",
    slug: "include-paths",
    title: "Included makefiles and path anchors",
  },
  {
    access: "paid",
    description:
      "Choose when a Make variable expands and understand why included files share one parsed namespace.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Flavors.html",
      title: "The Two Flavors of Variables",
    },
    lessonCommits: ["7bf2533", "6efe9dc", "6cd3401", "8f53b24", "9981476"],
    sectionId: "makefiles-and-parsing",
    slug: "variable-expansion",
    title: "Variable scope and expansion timing",
  },
  {
    access: "paid",
    description:
      "Use `MAKEFILES` as a scoped bootstrap while keeping recursive Make state from leaking into a fresh invocation.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/MAKEFILES-Variable.html",
      title: "The `MAKEFILES` Variable",
    },
    lessonCommits: ["9981476"],
    sectionId: "makefiles-and-parsing",
    slug: "makefiles-and-recursion",
    title: "MAKEFILES and recursive state",
  },
  {
    access: "free",
    description:
      "See how a rule connects a target, its prerequisites, and the recipe that can update it.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Rule-Introduction.html",
      title: "An Introduction to Makefile Rules",
    },
    lessonCommits: ["1f83aca", "7bf2533", "788dfba", "6cd3401"],
    sectionId: "rules-and-targets",
    slug: "rule-anatomy",
    title: "Rule anatomy",
  },
  {
    access: "paid",
    description:
      "Use phony targets for named build behavior that must never be confused with a file.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Phony-Targets.html",
      title: "Phony Targets",
    },
    lessonCommits: ["1f83aca", "7bf2533", "6cd3401"],
    sectionId: "rules-and-targets",
    slug: "phony-targets",
    title: "Phony targets",
  },
  {
    access: "paid",
    description:
      "Use `%` to describe a family of targets and automatic variables to refer to each concrete match.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Pattern-Rules.html",
      title: "Defining and Redefining Pattern Rules",
    },
    lessonCommits: ["788dfba", "73f70c4", "f9b85bc"],
    sectionId: "rules-and-targets",
    slug: "pattern-rules",
    title: "Pattern rules and automatic variables",
  },
  {
    access: "paid",
    description:
      "Model what makes a target stale while separating real inputs from prerequisites needed only for ordering.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Prerequisite-Types.html",
      title: "Types of Prerequisites",
    },
    lessonCommits: ["788dfba", "73f70c4", "f9b85bc"],
    sectionId: "rules-and-targets",
    slug: "prerequisite-freshness",
    title: "Prerequisites and freshness",
  },
  {
    access: "free",
    description:
      "Distinguish every target Make knows about from the goal selected to start a particular run.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Goals.html",
      title: "Arguments to Specify the Goals",
    },
    lessonCommits: ["1f83aca", "7bf2533", "788dfba"],
    sectionId: "goals-and-execution",
    slug: "targets-and-goals",
    title: "Targets and goals",
  },
  {
    access: "paid",
    description:
      "Follow how Make starts from a goal and recursively considers the prerequisites it can reach.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/How-Make-Works.html",
      title: "How make Processes a Makefile",
    },
    lessonCommits: ["1f83aca", "788dfba"],
    sectionId: "goals-and-execution",
    slug: "execution-order",
    title: "How Make walks the graph",
  },
  {
    access: "free",
    description:
      "Trace dollar signs, variables, and environment values across Make's recipe expansion and the shell that executes it.",
    officialReference: {
      href: "https://www.gnu.org/software/make/manual/html_node/Variables-in-Recipes.html",
      title: "Using Variables in Recipes",
    },
    lessonCommits: ["6cd3401", "8f53b24", "9981476"],
    sectionId: "recipes-and-shells",
    slug: "make-shell-boundary",
    title: "The Make-to-shell boundary",
  },
  {
    access: "paid",
    description:
      "Treat sourced POSIX shell code as shared state and use naming conventions to make collisions visible.",
    officialReference: {
      href: "https://pubs.opengroup.org/onlinepubs/9799919799/utilities/V3_chap02.html",
      label: "POSIX reference",
      title: "Shell Command Language",
    },
    lessonCommits: ["10c86e7", "73f70c4", "ad235d8"],
    sectionId: "recipes-and-shells",
    slug: "shell-shared-state",
    title: "Sourced shell state",
  },
  {
    access: "free",
    description:
      "Download into a temporary area, verify the artifact, and expose it only after the complete result is ready.",
    officialReference: {
      href: "https://www.gnu.org/software/coreutils/manual/html_node/mv-invocation.html",
      label: "GNU Coreutils manual",
      title: "`mv`: Move files",
    },
    lessonCommits: ["10c86e7", "73f70c4", "f9b85bc"],
    sectionId: "managed-installations",
    slug: "staged-installation",
    title: "Staged tool installation",
  },
  {
    access: "paid",
    description:
      "Keep installed releases additive and select one through stable symlink paths instead of overwriting it in place.",
    officialReference: {
      href: "https://pubs.opengroup.org/onlinepubs/9799919799/utilities/ln.html",
      label: "POSIX reference",
      title: "`ln`",
    },
    lessonCommits: ["f9b85bc"],
    sectionId: "managed-installations",
    slug: "versioned-releases",
    title: "Versioned releases and stable symlinks",
  },
  {
    access: "free",
    description:
      "Compose repository-wide, language, and area instructions without turning one entry document into a monolith.",
    lessonCommits: ["e4d02ef", "ad235d8", "6cb3b4f"],
    sectionId: "repository-practice",
    slug: "layered-conventions",
    title: "Layered repository conventions",
  },
] as const satisfies readonly ConceptDefinition[];

export function getConceptDefinition(
  slug: string | undefined,
): ConceptDefinition | undefined {
  return conceptDefinitions.find((concept) => concept.slug === slug);
}

export function isConceptSlug(slug: string | undefined): slug is ConceptSlug {
  return getConceptDefinition(slug) !== undefined;
}

export function getAdjacentConcepts(slug: ConceptSlug) {
  const index = conceptDefinitions.findIndex(
    (concept) => concept.slug === slug,
  );

  return {
    next: conceptDefinitions[index + 1],
    previous: conceptDefinitions[index - 1],
  };
}

export function getConceptsBySection(sectionId: ConceptSectionId) {
  return conceptDefinitions.filter((concept) => {
    return concept.sectionId === sectionId;
  });
}

export function getConceptSection(sectionId: ConceptSectionId) {
  return conceptSections.find((section) => section.id === sectionId);
}

export function getConceptDefinitionByHref(href: string) {
  const slug = href.split("#")[0]?.split("/").at(-1);
  return getConceptDefinition(slug);
}
